import { isCookiePresent } from 'Utils/BrowserCookies';

export const INITIAL_STEP = -1;

/**
 * OnBoardingEnabled function.
 * @typedef {Function} OnBoardingEnabled
 * @returns {boolean} Should we render this step?
 */

/**
 * OnBoardingStep interface.
 * @typedef {Object} OnBoardingStep
 * @property {string} id - Unique id for this step.
 * @property {number} order - In which order should we run this step?.
 * @property {OnBoardingEnabled} [enabled] - Function to decide if step is available or not.
 * @property {string} [localStorage] - Display once, track on localStorate.
 * @property {string} [cookie] - Display once, track on cookies.
 */

/**
 * OnBoardingState interface.
 * @typedef {Object} OnBoardingState
 * @property {number} current - Current step.
 * @property {OnBoardingStep[]} steps - All onBoarding steps registered.
 */

/**
 * Find the next available order
 * @function
 * @param {OnBoardingStep[]} steps The list of current steps
 * @param {number | undefined} startingPoint Current step
 * @returns {OnBoardingStep | null} The current step
 */
export const findNextStep = (steps, startingPoint = INITIAL_STEP) => {
  if (!steps?.length) return INITIAL_STEP;

  let nextIndex = startingPoint;
  let step = null;

  while (nextIndex < steps.length) {
    nextIndex += 1;
    step = steps[nextIndex];

    if (step) {
      const isEnabled = !step?.enabled || step.enabled();
      const isNotSeenCookies = !step?.cookie || !isCookiePresent(step.cookie);
      const isNotSeenLocalStorage = !step?.localStorage || !localStorage
        .getItem(step.localStorage);

      if (isEnabled && isNotSeenCookies && isNotSeenLocalStorage) break;
    }
  }

  return nextIndex === startingPoint || nextIndex >= steps.length ? INITIAL_STEP : nextIndex;
};

/**
 * Merge new steps into existing ones without duplicates
 * @function
 * @param {OnBoardingStep[]} currentSteps The list of current steps
 * @param {OnBoardingStep[]} newSteps New steps to add
 * @returns {OnBoardingStep[]} The merged steps
 */
export const mergeSteps = (currentSteps = [], newSteps = []) => {
  const addedSteps = {};

  currentSteps.forEach((step) => { addedSteps[step.id] = step; });
  newSteps.forEach((step) => {
    if (step?.id) addedSteps[step.id] = step;
  });

  return Object.values(addedSteps).sort((stepA, stepB) => {
    const aOrder = stepA.order ?? 0;
    const bOrder = stepB.order ?? 0;
    if (aOrder < bOrder) return -1;
    if (aOrder > bOrder) return 1;
    return 0;
  });
};

/**
 * Adds new step to current state and resets the onBoarding
 * @function
 * @param {OnBoardingState} state The list of current steps
 * @param {OnBoardingStep[]} newSteps New steps to add
 * @returns {OnBoardingState} The merged steps
 */
export const addStepsAndRestart = (state, newSteps) => {
  const mergedSteps = mergeSteps(state?.steps, newSteps);
  const current = findNextStep(mergedSteps, INITIAL_STEP);

  return {
    ...state,
    current,
    steps: mergedSteps,
  };
};
